import type { FontDto, FontStyleDto } from "@repo/api/font";
import { createContext, useContext, useEffect, useState } from "react";
import { continueRender, delayRender } from "remotion";

export const FontContext = createContext<Record<string, FontDto>>({});

// Tracks in-flight/loaded custom font variants, keyed by `${externalId}:${weight}:${style}`,
// so each specific font file is only fetched once.
const loadedCustomFonts = new Map<string, Promise<void>>();

function pickFontVariant(font: FontDto, weight: number, style: FontStyleDto) {
  const variants = font.metadata.variants;

  if (variants.length === 0) {
    return undefined;
  }

  const sameStyle = variants.filter((variant) => variant.style === style);
  const pool = sameStyle.length > 0 ? sameStyle : variants;

  return pool.reduce((closest, variant) =>
    Math.abs(variant.weight - weight) < Math.abs(closest.weight - weight)
      ? variant
      : closest,
  );
}

/**
 * Loads a single font file (the variant closest to the requested weight/style)
 * via the FontFace API and registers it under a unique CSS family so it can be
 * referenced without colliding with other fonts of the same family name.
 */
export async function loadCustomFont(
  font: FontDto,
  weight: number,
  style: FontStyleDto,
): Promise<string> {
  const cssFamily = `custom-font-${font.externalId}`;
  const key = `${font.externalId}:${weight}:${style}`;

  let loadPromise = loadedCustomFonts.get(key);

  if (!loadPromise) {
    loadPromise = (async () => {
      const variant = pickFontVariant(font, weight, style);

      if (!variant) {
        throw new Error(`No font variant available for ${font.family}`);
      }

      const fontFace = new FontFace(cssFamily, `url("${variant.url}")`, {
        weight: String(weight),
        style,
      });

      const loadedFontFace = await fontFace.load();
      document.fonts.add(loadedFontFace);
    })();

    loadedCustomFonts.set(key, loadPromise);
  }

  try {
    await loadPromise;
  } catch (error) {
    // Allow a later retry if this variant failed to load.
    loadedCustomFonts.delete(key);
    throw error;
  }

  return cssFamily;
}

export function useFont(options?: {
  fontExternalId?: string;
  fontWeight?: number;
  fontStyle?: FontStyleDto;
}): string | null {
  const fonts = useContext(FontContext);
  const fontExternalId = options?.fontExternalId;
  const fontWeight = options?.fontWeight ?? 400;
  const fontStyle = options?.fontStyle ?? "normal";
  const customFont = fontExternalId ? fonts[fontExternalId] : undefined;

  const [loadedFontFamily, setLoadedFontFamily] = useState<string | null>(null);
  const [delayHandle] = useState(() => delayRender());

  useEffect(() => {
    async function delayLoadFont() {
      // We only ever load our own custom fonts from S3 (the seeded library
      // fonts and team uploads). If the referenced font isn't available we fall
      // back to the raw family name rather than reaching out to Google Fonts.
      if (!customFont) {
        setLoadedFontFamily(null);
        continueRender(delayHandle);
        return;
      }

      try {
        const cssFamily = await loadCustomFont(
          customFont,
          fontWeight,
          fontStyle,
        );
        setLoadedFontFamily(cssFamily);
      } catch {
        setLoadedFontFamily(null);
      }

      continueRender(delayHandle);
    }

    delayLoadFont();
  }, [customFont, fontWeight, fontStyle, delayHandle]);

  return loadedFontFamily;
}
